/* eslint-disable web/require-page-view-tracking -- page-view tracking is delegated to
   <MarketingCrossDomainLogin>, which calls the injected analytics.trackPageView('/login', …)
   on mount (covered by @puffin-core/login-shell's MarketingCrossDomainLogin test). */
/**
 * @fileoverview MarketingLoginPage — cross-domain login on www.purchasepuffin.com.
 *
 * MKT-LOGIN-XDOMAIN-001 (2026-06-29): a thin purchase-web wrapper of
 * `@puffin-core/login-shell` `<MarketingCrossDomainLogin>` that injects the
 * PurchasePuffin brand + SEO + analytics + captcha + the shared
 * subdomain-persistence helper + the purchase auth hook. A TENANT admin enters
 * their company subdomain + credentials and is redirected to their own tenant
 * ({tenant}.purchasepuffin.com).
 *
 * @module portals/marketing-website/pages/MarketingLoginPage
 */

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MarketingCrossDomainLogin, createSubdomainPersistence } from '@puffin-core/login-shell';
import { CaptchaField } from '@puffin-core/captcha-ui';
import { MarketingSeo, signupUrlFor, useAnalytics, webPage } from '@puffin-core/marketing-shell';
import { getBrand } from '@puffin-core/brand-assets';
import useMarketingLoginAuth from '../hooks/useMarketingLoginAuth';

// Remembered-subdomain store, namespaced to match purchase-web's auth storage
// (`purchase-auth`). Created once at module scope — its reads/writes resolve
// `window.localStorage` lazily, so it is SSR/headless-safe.
const subdomainPersistence = createSubdomainPersistence({
  storageKey: 'purchase-auth_subdomain',
});

/**
 * purchase-web's cross-domain marketing-login page (www.purchasepuffin.com/login).
 *
 * Thin wrapper of `@puffin-core/login-shell` `<MarketingCrossDomainLogin>`.
 * Page-view tracking is delegated to that component (hence the
 * `web/require-page-view-tracking` disable above).
 *
 * @returns {React.JSX.Element} The cross-domain login surface.
 */
function MarketingLoginPage() {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const captchaRef = useRef(null);

  const schemaData = webPage({
    appKey: 'purchase',
    brand: getBrand('purchase'),
    // Canonical URL for the JSON-LD WebPage node (login is client-only, never
    // prerendered — so a window-derived href would only ever be this anyway).
    url: 'https://purchasepuffin.com/login',
    title: t('login.seo.schemaName'),
    description: t('login.seo.schemaDescription'),
  });

  const seoSlot = (
    <MarketingSeo
      title={t('login.pageTitle')}
      description={t('login.pageDescription')}
      canonical="https://purchasepuffin.com/login"
      ogType="website"
      jsonLd={[schemaData]}
    />
  );

  return (
    <MarketingCrossDomainLogin
      useAuth={useMarketingLoginAuth}
      t={t}
      appName="PurchasePuffin"
      appKey="purchase"
      baseDomain=".purchasepuffin.com"
      analytics={analytics}
      seoSlot={seoSlot}
      subdomainPersistence={subdomainPersistence}
      passwordResetCaptchaSlot={<CaptchaField ref={captchaRef} />}
      getCaptchaToken={() => captchaRef.current?.getToken() ?? null}
      startTrialHref={signupUrlFor({ app: 'publish' })}
    />
  );
}

export default MarketingLoginPage;
